// Option F — did they actually do it.
// Without this, a flat score reads as "the treatment failed" when it may be
// "the plan was never attempted". The two call for opposite responses, so the
// adherence figure belongs beside the score rather than three worksheets away.
import React from "react";
import { useTranslation } from "react-i18next";
import { Section, Empty, Meter, Label } from "@/components/clinical/kit";
import { adherenceTable, adherenceTone } from "@/config/clinical";

const DECISION_TONE = {
  continue: "bg-emerald-50 text-emerald-700",
  modify: "bg-amber-50 text-amber-800",
  stop: "bg-rose-50 text-rose-700",
};

export default function AdherencePanel({ worksheetsByStage }) {
  const { t } = useTranslation();
  const a = adherenceTable(worksheetsByStage);

  if (!a.hasPlan && a.months.length === 0)
    return (
      <Section title="Plan and adherence" sub="Worksheets 35 and 36–38" testid="adherence-panel">
        <Empty>No 90-day action plan has been written yet.</Empty>
      </Section>
    );

  const meanTone = adherenceTone(a.mean);

  return (
    <Section title="Plan and adherence" sub="Worksheet 35 against each month's review"
      testid="adherence-panel"
      right={a.mean != null && (
        <div className="text-right">
          <Label>Mean adherence</Label>
          <div className="font-serif text-2xl font-bold tabular-nums" style={{ color: meanTone.color }}
            data-testid="mean-adherence">
            {a.mean}<span className="text-sm text-stone-400">/10</span>
          </div>
          <div className="text-[11px]" style={{ color: meanTone.color }}>{t(meanTone.label)}</div>
        </div>
      )}>
      <div className="overflow-x-auto rounded-md border border-[#E5DFD3]">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#E8EFE9] text-left">
            <tr>
              <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Area")}</th>
              <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Agreed action")}</th>
              {a.months.map((m) => (
                <th key={m} className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t(m)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {a.rows.map((r, i) => (
              <tr key={i} className="border-t border-[#EFE9DE] align-top" data-testid={`adherence-row-${i}`}>
                <td className="px-3 py-2 font-medium text-stone-700">{t(r.label)}</td>
                <td className="px-3 py-2 text-stone-700">
                  {r.action || <span className="italic text-stone-400">{t("not set")}</span>}
                  {r.often && <div className="text-[11px] text-stone-500">{r.often}</div>}
                  {r.barrier && (
                    <div className="mt-1 text-[11px] text-amber-800">
                      <b>{t("Expected barrier")}:</b> {r.barrier}
                      {r.solution && <> · <b>{t("Their solution")}:</b> {r.solution}</>}
                    </div>
                  )}
                </td>
                {r.reviews.map((v) => {
                  const tone = adherenceTone(v.adherence);
                  return (
                    <td key={v.month} className="px-3 py-2">
                      {v.adherence == null && !v.benefit && !v.decision ? (
                        <span className="text-stone-300">—</span>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-mono text-base font-bold tabular-nums" style={{ color: tone.color }}>
                              {v.adherence ?? "—"}
                            </span>
                            <span className="text-[10px] text-stone-400">/10</span>
                          </div>
                          <div className="mt-0.5 w-20"><Meter value={v.adherence} color={tone.color} /></div>
                          {v.benefit && <div className="mt-1 text-[11px] text-stone-600">{v.benefit}</div>}
                          {v.decision && (
                            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${DECISION_TONE[v.decision] || "bg-stone-100 text-stone-600"}`}>
                              {t(v.decision)}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {a.months.length === 0 && (
        <div className="mt-2"><Empty>A plan exists but no monthly review has been filled in yet.</Empty></div>
      )}
      <p className="mt-2 text-[11px] text-stone-400">
        {t("Read adherence before concluding a domain has not responded — a low score with low adherence is a different problem from a low score with high adherence.")}
      </p>
    </Section>
  );
}
